import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ThumbsUp } from 'lucide-react';
import { z } from 'zod';
import {
  FormDateField,
  FormSelectField,
  FormTextareaField,
  FormTextField,
} from '@/components/common/FormDialog';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { formatNumber } from '@/utils/number';
import {
  DECISION_COMMENT_MIN_LENGTH,
  DONATION_COMMENT_MAX_LENGTH,
  PAYMENT_PLACE_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
} from '../constants/marketPromotion.constants';
import { useRecommendDonation, useRejectRecommendation } from '../hooks/useMpMutations';
import type {
  MpApprovalDetail,
  MpApprovalRow,
  RecommendDonationPayload,
} from '../types/marketPromotion.types';
import {
  findOptionId,
  findOptionName,
  isPicked,
  isTransactionRefNeeded,
  toDateFieldText,
  toNumberText,
  toOptionalText,
  WHOLE_NUMBER_PATTERN,
} from '../utils/donationFormValues';
import { DonationRows } from './DonationDetailsCard';
import { OWN_INSTITUTE_SOURCE_ROW } from './donationRows';
import { DonationFormCard } from './DonationFormCard';

const REJECT_COMMENT_MESSAGE = `Write at least ${String(DECISION_COMMENT_MIN_LENGTH)} characters to reject`;

function getRecommendFormSchema(maxAmount: number) {
  return z
    .object({
      approvedAmount: z
        .string()
        .trim()
        .regex(WHOLE_NUMBER_PATTERN, 'Enter the amount in taka')
        .refine((amount) => Number(amount) <= maxAmount, {
          message: `Enter at most ${TAKA_SYMBOL} ${formatNumber(maxAmount)}`,
        }),
      paymentTypeId: z.number().optional().refine(isPicked, { message: 'Pick the payment type' }),
      transactionRef: z.string(),
      approxSaleQty: z.string().trim().regex(WHOLE_NUMBER_PATTERN, 'Enter the approx sale'),
      paymentPlaceId: z.number().optional().refine(isPicked, { message: 'Pick where it was paid' }),
      witness: z.string().trim().min(1, 'Enter the witness'),
      totalStudent: z.string().trim().regex(WHOLE_NUMBER_PATTERN, 'Enter the number of students'),
      givenDate: z.string(),
      comment: z.string().trim().min(1, 'Write your comment').max(DONATION_COMMENT_MAX_LENGTH),
    })
    .superRefine((values, context) => {
      if (isTransactionRefNeeded(values.paymentTypeId) && values.transactionRef.trim() === '') {
        context.addIssue({
          code: 'custom',
          path: ['transactionRef'],
          message: 'Enter the transaction ID',
        });
      }
    });
}

type RecommendFormValues = z.infer<ReturnType<typeof getRecommendFormSchema>>;

function getRecommendDefaults(donation: MpApprovalRow): RecommendFormValues {
  return {
    approvedAmount: String(donation.approvedAmount),
    paymentTypeId: findOptionId(PAYMENT_TYPE_OPTIONS, donation.paymentType),
    transactionRef: donation.transactionRef ?? '',
    approxSaleQty: toNumberText(donation.approxSaleQty),
    paymentPlaceId: findOptionId(PAYMENT_PLACE_OPTIONS, donation.paymentPlace),
    witness: donation.witness ?? '',
    totalStudent: toNumberText(donation.totalStudent),
    givenDate: toDateFieldText(donation.givenDate),
    comment: donation.recommendedComments ?? '',
  };
}

function toRecommendPayload(values: RecommendFormValues): RecommendDonationPayload {
  const transactionRef = isTransactionRefNeeded(values.paymentTypeId)
    ? toOptionalText(values.transactionRef)
    : undefined;
  const givenDate = toOptionalText(values.givenDate);

  return {
    approvedAmount: Number(values.approvedAmount),
    paymentType: findOptionName(PAYMENT_TYPE_OPTIONS, values.paymentTypeId) ?? '',
    ...(transactionRef === undefined ? {} : { transactionRef }),
    approxSaleQty: Number(values.approxSaleQty),
    paymentPlace: findOptionName(PAYMENT_PLACE_OPTIONS, values.paymentPlaceId) ?? '',
    witness: values.witness.trim(),
    totalStudent: Number(values.totalStudent),
    ...(givenDate === undefined ? {} : { givenDate }),
    recommendedComments: values.comment.trim(),
  };
}

interface RecommendDonationFormProps {
  donation: MpApprovalDetail;
  onClose: () => void;
  onDone: () => void;
}

export function RecommendDonationForm({ donation, onClose, onDone }: RecommendDonationFormProps) {
  const recommendFormSchema = useMemo(
    () => getRecommendFormSchema(donation.approvedAmount),
    [donation.approvedAmount],
  );
  const form = useForm<RecommendFormValues>({
    resolver: zodResolver(recommendFormSchema),
    defaultValues: getRecommendDefaults(donation),
  });
  const recommendMutation = useRecommendDonation(onDone);
  const rejectMutation = useRejectRecommendation(onDone);
  const paymentTypeId = useWatch({ control: form.control, name: 'paymentTypeId' });

  function handleRecommend(values: RecommendFormValues) {
    recommendMutation.mutate({ detailId: donation.detailId, payload: toRecommendPayload(values) });
  }

  function handleReject() {
    const comment = form.getValues('comment').trim();
    form.clearErrors();
    if (comment.length < DECISION_COMMENT_MIN_LENGTH) {
      form.setError('comment', { message: REJECT_COMMENT_MESSAGE });
      return;
    }
    rejectMutation.mutate({ detailId: donation.detailId, payload: { comment } });
  }

  return (
    <DonationFormCard
      donation={donation}
      title="Recommend Donation"
      icon={ThumbsUp}
      form={form}
      onSubmit={handleRecommend}
      onClose={onClose}
      submitLabel="Recommend"
      isSubmitPending={recommendMutation.isPending}
      rejectAction={{ onClick: handleReject, isPending: rejectMutation.isPending }}
      paymentRows={
        <>
          <DonationRows donation={donation} rows={[OWN_INSTITUTE_SOURCE_ROW]} />
          <FormTextField
            control={form.control}
            name="approvedAmount"
            label="Amount"
            inputMode="numeric"
            leadingSymbol={TAKA_SYMBOL}
            hasTabularNumbers
            isRequired
          />
          <FormSelectField
            control={form.control}
            name="paymentTypeId"
            label="Payment Type"
            options={PAYMENT_TYPE_OPTIONS}
            isLoading={false}
            placeholder="Pick the payment type"
            isRequired
          />
          {isTransactionRefNeeded(paymentTypeId) ? (
            <FormTextField
              control={form.control}
              name="transactionRef"
              label="Transaction ID"
              hasTabularNumbers
              isRequired
            />
          ) : null}
          <FormTextField
            control={form.control}
            name="approxSaleQty"
            label="Approx Sale"
            inputMode="numeric"
            hasTabularNumbers
            isRequired
          />
          <FormSelectField
            control={form.control}
            name="paymentPlaceId"
            label="Payment Place"
            options={PAYMENT_PLACE_OPTIONS}
            isLoading={false}
            placeholder="Pick where it was paid"
            isRequired
          />
          <FormTextField control={form.control} name="witness" label="Witness" isRequired />
          <FormTextField
            control={form.control}
            name="totalStudent"
            label="Total Students"
            inputMode="numeric"
            hasTabularNumbers
            isRequired
          />
          <FormDateField control={form.control} name="givenDate" label="Given Date" isOptional />
        </>
      }
      decisionRows={
        <FormTextareaField
          control={form.control}
          name="comment"
          label="Your Comment"
          placeholder="Write the subject details and the total students"
          maxLength={DONATION_COMMENT_MAX_LENGTH}
          isRequired
        />
      }
      hasPictureFields
    />
  );
}
